import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { apiHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { Puzzle, Search, Loader2 } from 'lucide-react';
import { PluginCard } from '@/components/plugins/PluginCard';
import { categoryIcons } from '@/components/plugins/constants';

// Marketplace registry (not project-scoped). TODO: promote to qk
const AVAILABLE_PLUGINS_KEY = ['available-plugins'] as const;

export default function Plugins() {
  // When mounted at /projects/:projectId/plugins, the project is fixed by URL —
  // no dropdown, no auto-pick-first-project guesswork. The global /plugins route
  // (sidebar entry) still works via the dropdown fallback.
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const isProjectScoped = Boolean(routeProjectId);

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<string>(routeProjectId || '');

  const { data: pluginsData, isLoading } = useQuery({
    queryKey: AVAILABLE_PLUGINS_KEY,
    queryFn: () => apiHelpers.getAvailablePlugins(),
  });

  const {
    data: projectsData,
    isLoading: projectsLoading,
    error: projectsError,
  } = useQuery({
    queryKey: qk.projects(),
    queryFn: () => apiHelpers.getProjects({ limit: 100 }),
  });

  // Same key as ProjectDetail's installed-plugins list — one cache entry per
  // project, and PluginCard's install/uninstall invalidation refreshes both.
  const { data: installedData } = useQuery({
    queryKey: qk.projectPlugins(selectedProject),
    queryFn: () => apiHelpers.getProjectPlugins(selectedProject),
    enabled: Boolean(selectedProject),
  });

  const plugins = pluginsData?.data?.data || [];
  const projects = projectsData?.data?.data || [];
  const installedPlugins = installedData?.data?.data || [];
  const installedPluginIds = new Set(installedPlugins.map((p: any) => p.pluginId));
  const currentProject = projects.find((p: any) => p.id === selectedProject);

  // Auto-select first project when projects load and none is selected.
  // Skip when the URL fixes the project — guessing would override the user's intent.
  useEffect(() => {
    if (isProjectScoped) return;
    if (!selectedProject && projects.length > 0) {
      setSelectedProject(projects[0].id);
    }
  }, [projects, selectedProject, isProjectScoped]);

  const categories = ['analytics', 'marketing', 'communication', 'compliance', 'content', 'ai'];

  const filteredPlugins = plugins.filter((plugin: any) => {
    const matchesSearch =
      !search ||
      plugin.name.toLowerCase().includes(search.toLowerCase()) ||
      plugin.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !selectedCategory || plugin.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Installed plugins first, then available. Within each bucket keep registry order
  // so the marketplace stays predictable as installs/uninstalls happen.
  const sortedPlugins = [...filteredPlugins].sort((a: any, b: any) => {
    const aInstalled = installedPluginIds.has(a.id) ? 0 : 1;
    const bInstalled = installedPluginIds.has(b.id) ? 0 : 1;
    return aInstalled - bInstalled;
  });
  const installedCount = sortedPlugins.filter((p: any) => installedPluginIds.has(p.id)).length;
  const availableCount = sortedPlugins.length - installedCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Plugins</h1>
        <p className="text-muted-foreground">
          {isProjectScoped && currentProject
            ? `Browse and install plugins for ${currentProject.name}`
            : 'Extend your projects with powerful integrations'}
        </p>
      </div>

      {/* Project Selector — hidden when the URL already pins the project */}
      {!isProjectScoped && (
        <div className="bg-card rounded-xl border p-4">
          <label className="mb-2 block text-sm font-medium">Select Project to Manage Plugins</label>
          {projectsLoading ? (
            <div className="text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading projects...
            </div>
          ) : projectsError ? (
            <div className="text-destructive text-sm">
              Failed to load projects. Please check if the backend is running.
            </div>
          ) : projects.length === 0 ? (
            <div className="text-muted-foreground text-sm">
              No projects found. Create a project first.
            </div>
          ) : (
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 md:w-80"
            >
              <option value="">Select a project...</option>
              {projects.map((project: any) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 md:flex-row">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search plugins..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-background focus:ring-primary w-full rounded-lg border py-2 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              !selectedCategory
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted hover:bg-muted/80'
            }`}
          >
            All
          </button>
          {categories.map((cat) => {
            const Icon = categoryIcons[cat] || Puzzle;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium capitalize transition-colors ${
                  selectedCategory === cat
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                }`}
              >
                <Icon className="h-4 w-4" />
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* Plugins Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : filteredPlugins.length === 0 ? (
        <div className="bg-card rounded-xl border py-12 text-center">
          <Puzzle className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
          <h3 className="mb-2 font-semibold">No plugins found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filter</p>
        </div>
      ) : (
        <div className="space-y-8">
          {installedCount > 0 && (
            <section>
              <h2 className="text-muted-foreground mb-3 text-sm font-semibold uppercase tracking-wide">
                Installed ({installedCount})
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {sortedPlugins
                  .filter((p: any) => installedPluginIds.has(p.id))
                  .map((plugin: any) => (
                    <PluginCard
                      key={plugin.id}
                      plugin={plugin}
                      projectId={selectedProject}
                      isInstalled
                    />
                  ))}
              </div>
            </section>
          )}

          {availableCount > 0 && (
            <section>
              <h2 className="text-muted-foreground mb-3 text-sm font-semibold uppercase tracking-wide">
                Available to install ({availableCount})
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {sortedPlugins
                  .filter((p: any) => !installedPluginIds.has(p.id))
                  .map((plugin: any) => (
                    <PluginCard
                      key={plugin.id}
                      plugin={plugin}
                      projectId={selectedProject}
                      isInstalled={false}
                    />
                  ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
